import React, { useState, useEffect, useMemo } from 'react';
import './Dashboard.scss';
import { NavLink } from 'react-router-dom';
import { Repeat } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { formatDateWith12HourTime, formatDateTime12Hour } from '../../shared/utils/dateUtils';
import { getWeeklyDailySummaries } from '../../core/API/workplace-daily-summaries';
import {
	Button,
	CircularProgress,
	Grid,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from '@mui/material';
import { getAllOutstandingTodos } from '../../core/API/todo';
import { getAllOutstandingChecklists, getChecklistTemplateById } from '../../core/API/checklist';
import { LocalEvents } from '../../shared/components/LocalEvents';
import { LocalEvent } from '../../core/API/local-events';
import { HotGame } from '../../core/API/hotGames';
import EventDetailView from '../../shared/components/EventDetailView/EventDetailView';
import { usePermissionCheck } from '../../core/hooks/usePermissionCheck';
import { useUserAssignmentGroupIds } from '../../core/hooks/useUserAssignmentGroupIds';
import { useWorkplaceUser } from '../../module/documents/utils';
import { useUserData } from '../../store/useUserData';
import { isChecklistEventAssignedToCurrentUser } from '../../core/utils/checklistEventAssignment';
import { filterTodosForMyScope } from '../../core/utils/todoAssignment';
import { isRecurringTodo, normalizeAndSortTodos, type TodoTableRow } from '../../core/utils/todoScheduleEvent';
import { sortChecklistEventsByScheduledDateAsc } from '../../core/utils/checklistEventSort';
import { SecurityRolePermission } from 'core/services/permissionService';
import { resolveDashboardVisibility } from './utils/dashboardWidgets';
import { getCurrentUserId } from './utils/homePage';
import { ROUTES } from '../navigation/enums/routes.enums';
import OutstandingChecklistView from '../checklist/components/OutstandingChecklistView';
import { getTodoDetailLocation } from '../toDoPage/utils/todoNavigation';
import { useToDoPermissions } from '../toDoPage/hooks/useToDoPermissions';
import PastDueDueDate, { dueDateTableCellSx } from './components/PastDueDueDate';
import { isChecklistPastDue, isTodoPastDue } from './utils/pastDueDisplay';

import HotGames from '../../components/featured/hot-games/HotGames';

const formatDateTime = (dateString: string) => {
	return formatDateWith12HourTime(dateString);
};

const createDefaultSalesData = () =>
	Array.from({ length: 7 }, (_, i) => {
		const date = new Date();
		date.setDate(date.getDate() - (7 - i));
		return {
			day: date.toLocaleDateString('en-US', { weekday: 'short' }),
			sales: 0,
			customers: 0,
		};
	});

// Interface for checklist events
interface ChecklistScheduleEvent {
	id: number;
	checklistTemplate: {
		id?: number;
		label: string;
		version: number;
		type?: string;
	};
	checklistSchedule?: {
		id: number;
		assignmentType: string;
		assignee?: {
			id: number;
			firstName: string;
			middleName?: string;
			lastName: string;
		} | null;
		group?: {
			id: number;
			label: string;
		} | null;
	};
	workplace?: {
		id: number;
		name: string;
	};
	organization?: {
		id: number;
		name: string;
	} | null;
	scheduledDate: string;
	scheduledTime?: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	status: string;
	completedBy: any;
	completedAt: any;
}

interface Todo extends TodoTableRow {}

const Dashboard = () => {
	const { hasModuleViewPermission } = usePermissionCheck();
	const { userOrg } = useWorkplaceUser();
	const { currentUserRole } = useUserData();
	const products = useMemo(() => userOrg?.products || [], [userOrg?.products]);

	const rolePermissions = useMemo(
		() => (currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
		[currentUserRole],
	);

	const visibility = React.useMemo(
		() => resolveDashboardVisibility(rolePermissions, products),
		[rolePermissions, products],
	);

	const canViewDashboard = hasModuleViewPermission('Home');
	const canViewChecklistAll = visibility.canViewChecklistAll;
	const canViewChecklistMy = visibility.canViewChecklistMy;
	const canViewToDoAll = visibility.canViewTodoAll;
	const canViewToDoMy = visibility.canViewTodoMy;
	const { canEdit: canEditToDo } = useToDoPermissions();
	const canViewSalesChart = visibility.canViewSalesChart;
	const canViewLocalEvents = visibility.canViewLocalEvents;
	const canViewHotGames = visibility.canViewHotGames;
	const canViewChecklistWidget = canViewChecklistAll || canViewChecklistMy;
	const canViewTodoWidget = canViewToDoAll || canViewToDoMy;
	const workplaceId = Number(localStorage.getItem('workplace-id') || 1);
	const currentUserId = getCurrentUserId();
	const currentSecurityRoleId = currentUserRole?.id ?? null;
	const needsAssignmentGroups = canViewChecklistWidget || canViewTodoWidget;

	const { userGroupIds, isLoading: groupsLoading } = useUserAssignmentGroupIds(
		workplaceId,
		needsAssignmentGroups,
	);

	const [upcomingChecklists, setUpcomingChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [allOutstandingTodos, setAllOutstandingTodos] = useState<Todo[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedChecklistItem, setSelectedChecklistItem] = useState<ChecklistScheduleEvent | null>(null);
	const [showChecklistDetail, setShowChecklistDetail] = useState(false);
	const [checklistDetailLoading, setChecklistDetailLoading] = useState(false);

	// Event detail view state
	const [selectedEvent, setSelectedEvent] = useState<LocalEvent | HotGame | null>(null);
	const [eventType, setEventType] = useState<'local-event' | 'hot-game' | null>(null);
	const [showEventDetail, setShowEventDetail] = useState(false);

	// Helper function to get description from various possible fields
	const getDescriptionDisplay = (todo: Todo): string => {
		let noteValue: string | undefined;
		if (typeof todo.note === 'string') {
			noteValue = todo.note;
		} else if (todo.note && typeof todo.note === 'object') {
			const note = todo.note as Record<string, unknown>;
			noteValue =
				typeof note.message === 'string'
					? note.message
					: typeof note.description === 'string'
						? note.description
						: undefined;
		}

		const candidates = [todo?.description, todo?.label, todo?.name, todo?.title, noteValue];
		return candidates.find((v) => typeof v === 'string' && v.trim() !== '') || 'N/A';
	};

	// Helper function to format todo date with time
	const formatTodoDateWithTime = (todo: Todo): string => {
		const dueDateRaw = todo.due_date ?? todo.dueDate;
		const dueTime = todo.dueTime;
		return formatDateTime12Hour(dueDateRaw, dueTime);
	};

	// Helper function to get assigned display for checklist
	const getChecklistAssignedDisplay = (checklist: ChecklistScheduleEvent): string => {
		const schedule = checklist.checklistSchedule;
		if (!schedule) return 'N/A';

		// If assignment type is group, show group label
		if (schedule.assignmentType === 'group') {
			return schedule.group?.label || 'Unassigned Group';
		}

		// If assignment type is assignee, show assignee's firstName and lastName
		if (schedule.assignmentType === 'assignee') {
			return schedule.assignee
				? `${schedule.assignee.firstName} ${schedule.assignee.lastName}`.trim()
				: 'Unassigned';
		}

		// Fallback for any other assignment type
		return 'Unassigned';
	};

	// Helper function to get owner display for checklist
	const getChecklistOwnerDisplay = (checklist: ChecklistScheduleEvent): string => {
		if (checklist.checklistTemplate?.type === 'organization') {
			return checklist.organization?.name || 'Organization';
		}
		return checklist.workplace?.name || 'Workplace';
	};

	       const getTodoAssignedDisplay = (todo: Todo): string => {
		       if (todo?.user && typeof todo.user === 'object') {
			       const user = todo.user as Record<string, unknown>;
			       const parts = [user.firstName, user.middleName, user.lastName].filter(
				       (p) => typeof p === 'string' && p.trim() !== '',
			       );
			       return parts.join(' ') || 'Unknown User';
		       }
		       if (todo.assignedGroup && typeof todo.assignedGroup === 'object') {
			       const group = todo.assignedGroup as Record<string, unknown>;
			       return (group.label as string) || 'Group Assignment';
		       }
		       if (todo.assigned) {
			       return todo.assigned;
		       }
		       return 'Unassigned';
	       };

	// Helper function to get status display for todo
	const getTodoStatusDisplay = (todo: Todo): string => {
		return todo.status?.label || todo.status?.name || 'N/A';
	};

	const getTodoCategoryDisplay = (todo: Todo): string => {
		if (!todo.category) return 'Uncategorized';
		if (typeof todo.category === 'string') return todo.category;
		if (typeof todo.category === 'object') {
			const category = todo.category as Record<string, unknown>;
			return (
				(category.label as string) ||
				(category.name as string) ||
				(category.title as string) ||
				'Uncategorized'
			);
		}
		return 'Uncategorized';
	};

	const renderTodoDescriptionCell = (todo: Todo) => {
		const description = getDescriptionDisplay(todo);
		const content = todo.isScheduleEvent || isRecurringTodo(todo) ? (
			<Box display="flex" alignItems="center" gap={0.75}>
				<Repeat sx={{ fontSize: 14, color: 'text.secondary' }} />
				<span>{description}</span>
			</Box>
		) : (
			description
		);

		return (
			<NavLink
				to={getTodoDetailLocation(todo.id, {
					returnTo: ROUTES.HOMEPAGE,
					canEdit: canEditToDo,
				})}
				className="dashboard-widget-link"
			>
				{content}
			</NavLink>
		);
	};

	const handleChecklistClick = async (item: ChecklistScheduleEvent): Promise<void> => {
		if (!item.checklistTemplate?.id) {
			setSelectedChecklistItem(item);
			setShowChecklistDetail(true);
			return;
		}

		setChecklistDetailLoading(true);
		try {
			const detailedTemplate = await getChecklistTemplateById(item.checklistTemplate.id);
			setSelectedChecklistItem({
				...item,
				checklistTemplate: {
					...item.checklistTemplate,
					...detailedTemplate,
					tasks: detailedTemplate.tasks || [],
				},
			});
			setShowChecklistDetail(true);
		} catch (error) {
			console.error('Error fetching checklist template:', error);
			setSelectedChecklistItem(item);
			setShowChecklistDetail(true);
		} finally {
			setChecklistDetailLoading(false);
		}
	};

	const handleChecklistDetailBack = () => {
		setShowChecklistDetail(false);
		setSelectedChecklistItem(null);
	};

	// Event click handlers
	const handleLocalEventClick = (event: LocalEvent) => {
		setSelectedEvent(event);
		setEventType('local-event');
		setShowEventDetail(true);
	};

	const handleHotGameClick = (game: HotGame) => {
		setSelectedEvent(game);
		setEventType('hot-game');
		setShowEventDetail(true);
	};

	const handleCloseEventDetail = () => {
		setShowEventDetail(false);
		setSelectedEvent(null);
		setEventType(null);
	};

	// Helper function to check if checklist is assigned to current user or their groups
	const isAssignedToCurrentUser = (checklist: ChecklistScheduleEvent): boolean =>
		isChecklistEventAssignedToCurrentUser(
			checklist,
			currentUserId,
			userGroupIds,
			currentSecurityRoleId,
		);

	// Filter checklists based on permissions
	const checklistsToShow = canViewChecklistAll
		? upcomingChecklists
		: canViewChecklistMy
			? upcomingChecklists.filter(isAssignedToCurrentUser)
			: [];
	// ('Checklists to show:', checklistsToShow);
	// Fetch checklist and todo data when component mounts
	useEffect(() => {
		if (needsAssignmentGroups && groupsLoading) {
			return;
		}

		const fetchData = async () => {
			try {
				const outstandingChecklists = sortChecklistEventsByScheduledDateAsc(
					await getAllOutstandingChecklists(workplaceId),
				);
				const outstandingTodosRaw = await getAllOutstandingTodos(workplaceId);
				const scopedOutstandingTodos = canViewToDoAll
					? outstandingTodosRaw
					: canViewToDoMy
						? filterTodosForMyScope(
								outstandingTodosRaw,
								currentUserId,
								userGroupIds,
								currentSecurityRoleId,
							)
						: [];
				const allOutstandingTodosData = normalizeAndSortTodos(
					scopedOutstandingTodos,
				) as Todo[];

				setUpcomingChecklists(outstandingChecklists);
				setAllOutstandingTodos(allOutstandingTodosData);
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [
		workplaceId,
		canViewToDoAll,
		canViewToDoMy,
		canViewChecklistAll,
		canViewChecklistMy,
		canViewChecklistWidget,
		canViewTodoWidget,
		currentUserId,
		currentSecurityRoleId,
		userGroupIds,
		needsAssignmentGroups,
		groupsLoading,
	]);

	const [salesData, setSalesData] = useState<{ day: string; sales: number; customers: number }[]>(
		createDefaultSalesData
	);

	// Fetch sales and traffic data
	useEffect(() => {
		if (!canViewSalesChart) return;

		const fetchSalesData = async () => {
			try {
				// Generate last 7 days excluding today
				const last7Days = Array.from({ length: 7 }, (_, i) => {
					const date = new Date();
					date.setDate(date.getDate() - (7 - i));
					return {
						date: date.toISOString().split('T')[0],
						day: date.toLocaleDateString('en-US', { weekday: 'short' }),
						sales: 0,
						customers: 0,
					};
				});

				const response = await getWeeklyDailySummaries(workplaceId);

				// Merge API data with the 7-day template
				const mergedData = last7Days.map((day) => {
					const apiData = response.member?.find((item) => item.date === day.date);
					return {
						day: day.day,
						sales: apiData?.salesTotal || 0,
						customers: apiData?.trafficTotal || 0,
					};
				});

				setSalesData(mergedData);
			} catch (error) {
				console.error('Error fetching sales data:', error);
				setSalesData(createDefaultSalesData());
			}
		};

		fetchSalesData();
	}, [workplaceId, canViewSalesChart]);

	// Check permissions
	if (!canViewDashboard) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '400px',
					p: 3,
				}}
			>
				<Typography variant="h6" color="textSecondary">
					You do not have permission to view Home.
				</Typography>
			</Box>
		);
	}

	// Show checklist detail view if a checklist is selected
	if (showChecklistDetail && selectedChecklistItem) {
		return (
			<div className="dashboard-container">
				<Paper elevation={3} sx={{ p: 2, backgroundColor: 'whitesmoke' }}>
					<Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
						<Button
							variant="outlined"
							onClick={handleChecklistDetailBack}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: 'white',
								padding: '5px 12px',
								'&:hover': { backgroundColor: 'rgb(56, 82, 114)' },
							}}
						>
							Back
						</Button>
					</Box>
					{checklistDetailLoading ? (
						<Box display="flex" justifyContent="center" p={4}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<OutstandingChecklistView selectedItem={selectedChecklistItem} />
					)}
				</Paper>
			</div>
		);
	}

	// Show event detail view if an event is selected
	if (showEventDetail && selectedEvent && eventType) {
		return (
			<EventDetailView
				event={selectedEvent}
				onClose={handleCloseEventDetail}
				eventType={eventType}
			/>
		);
	}

	return (
		<div className="dashboard-container">
			{/* Top Section */}
			<div className="top-section">
				<Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
					{canViewSalesChart && (
					<Grid item xs={12} md={8} className="dashboard-sales-chart-column">
						<div className="card sales-chart">
							<Typography variant="h6" className="dashboard-card-title">
								Rolling 7 Days
							</Typography>
							<div className="dashboard-charts-container">
								<div className="dashboard-chart-section">
									<Typography variant="h6" className="dashboard-chart-title">
										Daily Sales ($)
									</Typography>
									<div className="dashboard-chart-wrapper">
										<ResponsiveContainer width="100%" height={250}>
											<BarChart
												data={salesData}
												margin={{
													top: 30,
													right: 30,
													left: 20,
													bottom: 5,
												}}
											>
												<XAxis dataKey="day" />
												<YAxis />
												<Tooltip />
												<Bar dataKey="sales" fill="#436183">
													<LabelList
														dataKey="sales"
														position="top"
														formatter={(value) => `$${value ?? 0}`}
														style={{ fontSize: '12px' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>

								<div className="dashboard-chart-section">
									<Typography variant="h6" className="dashboard-chart-title">
										Daily Customers
									</Typography>
									<div className="dashboard-chart-wrapper">
										<ResponsiveContainer width="100%" height={250}>
											<BarChart
												data={salesData}
												margin={{
													top: 30,
													right: 30,
													left: 20,
													bottom: 5,
												}}
											>
												<XAxis dataKey="day" />
												<YAxis />
												<Tooltip />
												<Bar dataKey="customers" fill="#436183">
													<LabelList
														dataKey="customers"
														position="top"
														formatter={(value) => value ?? 0}
														style={{ fontSize: '12px' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>
							</div>
						</div>
					</Grid>
					)}
					{(canViewLocalEvents || canViewHotGames) && (
					<Grid item xs={12} md={canViewSalesChart ? 4 : 12}>
						<Grid
							container
							direction="column"
							spacing={2}
							className="dashboard-side-stack"
						>
							<Grid item xs={6}>
								{canViewLocalEvents && (
								<div className="card alerts-dashboard">
									<div className="events-header">
										<Typography variant="h6" className="dashboard-card-title">
											Mabel A.I. - Local Events
										</Typography>
									</div>
									<LocalEvents workplaceId={workplaceId} onEventClick={handleLocalEventClick} />
								</div>
								)}
							</Grid>
							<Grid item xs={6}>
								{canViewHotGames && (
								<HotGames onGameClick={handleHotGameClick} />
								)}
							</Grid>
						</Grid>
					</Grid>
					)}
				</Grid>
			</div>

			{/* Middle Section */}
			{(canViewChecklistWidget || canViewTodoWidget) && (
			<div className="middle-section">
				{canViewChecklistWidget && (
				<TableContainer component={Paper} className="card dashboard-outstanding-widget">
					<Typography variant="h6" className="dashboard-card-title" sx={{ px: 2 }}>
					All Outstanding Checklists
					</Typography>
					<div className="dashboard-table-scroll">
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Checklist Name
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Owner</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										backgroundColor: '#f5f5f5',
										...dueDateTableCellSx,
									}}
								>
									Checklist Due
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
							) : checklistsToShow.length > 0 ? (
								checklistsToShow.map((checklist) => (
									<TableRow
										key={checklist.id}
										className="dashboard-widget-clickable-row"
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell
											onClick={() => handleChecklistClick(checklist)}
											className="dashboard-widget-link-cell"
										>
											{checklist.checklistTemplate.label}
										</TableCell>
										<TableCell>{getChecklistAssignedDisplay(checklist)}</TableCell>
										<TableCell>{getChecklistOwnerDisplay(checklist)}</TableCell>
									<TableCell sx={dueDateTableCellSx}>
										<PastDueDueDate
											formattedDate={
												checklist.scheduledDateFormatted && checklist.scheduledTimeFormatted
													? formatDateTime12Hour(
															checklist.scheduledDateFormatted,
															checklist.scheduledTimeFormatted,
														)
													: formatDateTime(checklist.scheduledDate)
											}
											isPastDue={isChecklistPastDue(checklist)}
										/>
									</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No outstanding checklists
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
					</div>
				</TableContainer>
				)}

				{canViewTodoWidget && (
				<TableContainer component={Paper} className="card dashboard-outstanding-widget">
					<Typography variant="h6" className="dashboard-card-title" sx={{ px: 2 }}>
					All Outstanding To-Dos
					</Typography>
					<div className="dashboard-table-scroll">
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Description
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '18%' }}>
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '14%' }}>
									Category
								</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										backgroundColor: '#f5f5f5',
										whiteSpace: 'nowrap',
										width: '1%',
									}}
								>
									Status
								</TableCell>
								<TableCell
									sx={{
										fontWeight: 'bold',
										backgroundColor: '#f5f5f5',
										...dueDateTableCellSx,
									}}
								>
									Due Date
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
						) : allOutstandingTodos.length > 0 ? (
							allOutstandingTodos.map((todo) => (
									<TableRow
										key={todo.id}
										className="dashboard-widget-clickable-row"
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell>{renderTodoDescriptionCell(todo)}</TableCell>
										<TableCell>{getTodoAssignedDisplay(todo)}</TableCell>
										<TableCell>{getTodoCategoryDisplay(todo)}</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											{getTodoStatusDisplay(todo)}
										</TableCell>
										<TableCell sx={dueDateTableCellSx}>
											<PastDueDueDate
												formattedDate={formatTodoDateWithTime(todo)}
												isPastDue={isTodoPastDue(todo)}
											/>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										No outstanding to-dos
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
					</div>
				</TableContainer>
				)}
			</div>
			)}
		</div>
	);
};

export default Dashboard;
